import { Component, type ReactNode, useEffect, useMemo } from 'react';

import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { FocusAttributeProvider } from '@/dialogs/item-editor/Context/FocusAttributeProvider';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { useDataModel } from '@/hooks/useDataModel';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { DataModel, DataModelAttribute } from '@/types/DataModel';

const readContactAttributes = (dataModel: DataModel | undefined): DataModelAttribute[] => {
    const model = dataModel as (DataModel & { getContactAttributes?: () => DataModelAttribute[] }) | undefined;
    return model?.getContactAttributes?.() ?? [];
};

const hasEditLayout = (dataModel: DataModel | undefined): boolean => {
    try {
        return Boolean(dataModel?.hasLayout('edit'));
    } catch {
        return false;
    }
};

class InvitationAttributeBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError(): { failed: boolean } {
        return { failed: true };
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

const InvitationCustomAttributes = () => {
    const { dataModel } = useDataModel();
    const attributes = useMemo(() => readContactAttributes(dataModel), [dataModel]);
    const editLayoutAvailable = useMemo(() => hasEditLayout(dataModel), [dataModel]);

    useEffect(() => {
        if (attributes.length === 0) {
            return;
        }
        try {
            getRootScope(getInjector()).prepareAttributes?.(attributes);
        } catch {
            // Attribute editors still render without the Angular prepare step.
        }
    }, [attributes]);

    if (attributes.length === 0 || !editLayoutAvailable) {
        return null;
    }

    return (
        <InvitationAttributeBoundary>
            <FocusAttributeProvider>
                <FormResetProvider>
                    <LayoutProvider layoutName="edit">
                        {attributes.map((attribute) => (
                            <AttributeComponent
                                key={attribute.name}
                                attribute={attribute}
                                disableUrlSync
                                direction="vertical"
                            />
                        ))}
                    </LayoutProvider>
                </FormResetProvider>
            </FocusAttributeProvider>
        </InvitationAttributeBoundary>
    );
};

export { InvitationCustomAttributes, readContactAttributes };
